import React, { useEffect, useState } from 'react';
import apiClient from '../apiClient';

interface Organization {
  id: number;
  name: string;
  slug: string;
  is_active: boolean;
}

const emptyForm = {
  id: null as number | null,
  name: '',
  slug: '',
  is_active: true,
};

const OrganizationsPage: React.FC = () => {
  const [items, setItems] = useState<Organization[]>([]);
  const [form, setForm] = useState<typeof emptyForm>(emptyForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadOrganizations = () => {
    apiClient.get('/organizations').then(res => {
      setItems(res.data.data.items as Organization[]);
    });
  };

  useEffect(() => {
    loadOrganizations();
  }, []);

  const startCreate = () => {
    setError(null);
    setForm(emptyForm);
  };

  const startEdit = (org: Organization) => {
    setError(null);
    setForm({
      id: org.id,
      name: org.name,
      slug: org.slug,
      is_active: org.is_active,
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target;
    const { name } = target;

    let value: string | boolean = target.value;
    if (target.type === 'checkbox') {
      value = target.checked;
    }

    setForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (form.id == null) {
        await apiClient.post('/organizations', {
          name: form.name,
          slug: form.slug,
          is_active: form.is_active,
        });
      } else {
        await apiClient.put(`/organizations/${form.id}`, {
          name: form.name,
          slug: form.slug,
          is_active: form.is_active,
        });
      }
      setForm(emptyForm);
      loadOrganizations();
    } catch (err) {
      setError('Error guardando la organización');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#020617',
        padding: '8px 8px 24px',
        color: '#e5e7eb',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
      }}
    >
      <header style={{ marginBottom: 16 }}>
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: 0.3,
            color: '#f9fafb',
          }}
        >
          Organizaciones
        </h1>
        <p
          style={{
            margin: '4px 0 0',
            fontSize: 13,
            color: '#9ca3af',
          }}
        >
          Gestiona las organizaciones que forman parte de tus auditorías.
        </p>
      </header>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.3fr) minmax(0, 2fr)',
          gap: 16,
          alignItems: 'flex-start',
        }}
      >
        <section
          style={{
            padding: 16,
            borderRadius: 18,
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            border: '1px solid rgba(148, 163, 184, 0.35)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 10,
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 600,
                color: '#e5e7eb',
              }}
            >
              {form.id == null ? 'Nueva organización' : `Editar organización #${form.id}`}
            </h2>
            <button
              type="button"
              onClick={startCreate}
              style={{
                padding: '6px 10px',
                borderRadius: 999,
                border: '1px solid rgba(148, 163, 184, 0.7)',
                backgroundColor: 'transparent',
                color: '#e5e7eb',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Nueva
            </button>
          </div>

          <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 10 }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: 4,
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  Nombre
                </label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  placeholder="Nombre de la organización"
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    borderRadius: 12,
                    border: '1px solid rgba(148, 163, 184, 0.6)',
                    backgroundColor: 'rgba(15, 23, 42, 0.85)',
                    color: '#e5e7eb',
                    fontSize: 13,
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ marginBottom: 10 }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: 4,
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  Slug
                </label>
                <input
                  name="slug"
                  value={form.slug}
                  onChange={handleChange}
                  required
                  placeholder="Identificador único (slug)"
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    borderRadius: 12,
                    border: '1px solid rgba(148, 163, 184, 0.6)',
                    backgroundColor: 'rgba(15, 23, 42, 0.85)',
                    color: '#e5e7eb',
                    fontSize: 13,
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ marginBottom: 10 }}>
                <label
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 12,
                  }}
                >
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={form.is_active}
                    onChange={handleChange}
                    style={{ cursor: 'pointer' }}
                  />
                  Activa
                </label>
              </div>

              {error && (
                <div
                  style={{
                    color: '#fecaca',
                    background: 'rgba(220, 38, 38, 0.12)',
                    border: '1px solid rgba(248, 113, 113, 0.4)',
                    borderRadius: 10,
                    padding: '6px 8px',
                    fontSize: 12,
                    marginBottom: 8,
                  }}
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  marginTop: 4,
                  padding: '8px 14px',
                  borderRadius: 999,
                  border: 'none',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: loading ? 'default' : 'pointer',
                  color: '#0b1120',
                  background:
                    loading
                      ? 'linear-gradient(to right, #6b7280, #4b5563)'
                      : 'linear-gradient(135deg, #22c55e, #16a34a, #22c55e)',
                  boxShadow: loading
                    ? '0 0 0 rgba(0,0,0,0)'
                    : '0 12px 30px rgba(22, 163, 74, 0.55)',
                  opacity: loading ? 0.9 : 1,
                }}
              >
                {loading ? 'Guardando...' : form.id == null ? 'Crear organización' : 'Guardar cambios'}
              </button>
            </form>
        </section>

        <section
          style={{
            padding: 16,
            borderRadius: 18,
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            border: '1px solid rgba(148, 163, 184, 0.35)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 10,
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 600,
                color: '#e5e7eb',
              }}
            >
              Listado de organizaciones
            </h2>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>
              Total: {items.length}
            </span>
          </div>

          {items.length === 0 ? (
            <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>
              No hay organizaciones registradas.
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 12,
                }}
              >
                <thead>
                  <tr
                    style={{
                      textAlign: 'left',
                      borderBottom: '1px solid rgba(55, 65, 81, 0.8)',
                    }}
                  >
                    <th style={{ padding: '6px 4px' }}>ID</th>
                    <th
                      style={{
                        padding: '6px 4px',
                        maxWidth: 180,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      Nombre
                    </th>
                    <th
                      style={{
                        padding: '6px 4px',
                        maxWidth: 180,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      Slug
                    </th>
                    <th style={{ padding: '6px 4px' }}>Activa</th>
                    <th style={{ padding: '6px 4px' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(org => (
                    <tr
                      key={org.id}
                      style={{ borderBottom: '1px solid rgba(31, 41, 55, 0.9)' }}
                    >
                      <td style={{ padding: '6px 4px' }}>{org.id}</td>
                      <td
                        style={{
                          padding: '6px 4px',
                          maxWidth: 180,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={org.name}
                      >
                        {org.name}
                      </td>
                      <td
                        style={{
                          padding: '6px 4px',
                          maxWidth: 180,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={org.slug}
                      >
                        {org.slug}
                      </td>
                      <td style={{ padding: '6px 4px' }}>{org.is_active ? 'Sí' : 'No'}</td>
                      <td style={{ padding: '6px 4px' }}>
                        <button
                          type="button"
                          onClick={() => startEdit(org)}
                          style={{
                            padding: '4px 8px',
                            borderRadius: 999,
                            border: '1px solid rgba(148, 163, 184, 0.7)',
                            backgroundColor: 'transparent',
                            color: '#e5e7eb',
                            fontSize: 11,
                            cursor: 'pointer',
                          }}
                        >
                          Editar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default OrganizationsPage;
